// src/pages/payments/components/PaymentRecordNotesModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, X, Send, Loader2 } from 'lucide-react';
import { getPaymentRecordNotes, addPaymentRecordNote } from '../../../services/api/paymentVerification';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { formatDateTime } from '../../../utils/dateUtils';
import styles from './PaymentRecordNotesModal.module.css';

const PaymentRecordNotesModal = ({ isOpen, onClose, record }) => {
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchNotes = useCallback(async () => {
    if (!record) return;
    try {
      setIsLoading(true);
      const response = await getPaymentRecordNotes(record.id);
      setNotes(response.notes || []);
    } catch (err) {
      console.error('Failed to fetch payment record notes:', err);
      showError(t('paymentVerification.notesLoadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [record, showError, t]);

  useEffect(() => {
    if (isOpen) {
      setNoteText('');
      fetchNotes();
    }
  }, [isOpen, fetchNotes]);

  if (!isOpen || !record) return null;

  const handleAddNote = async () => {
    const trimmed = noteText.trim();
    if (!trimmed) return;

    try {
      setIsSubmitting(true);
      const response = await addPaymentRecordNote(record.id, trimmed);
      setNotes((prev) => [response.note, ...prev]);
      setNoteText('');
    } catch (err) {
      console.error('Failed to add payment record note:', err);
      showError(err.response?.data?.message || t('paymentVerification.noteAddFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <MessageSquare size={18} />
            <h3 className={styles.title}>{t('paymentVerification.notes')}</h3>
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className={styles.body}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <Loader2 size={22} className={styles.spinnerIcon} />
            </div>
          ) : notes.length === 0 ? (
            <div className={styles.emptyState}>{t('paymentVerification.noNotesYet')}</div>
          ) : (
            <div className={styles.notesList}>
              {notes.map((note) => (
                <div key={note.id} className={styles.noteItem}>
                  <div className={styles.noteMeta}>
                    <span className={styles.noteAuthor}>{note.createdBy?.name || '-'}</span>
                    <span className={styles.noteDate}>{formatDateTime(note.createdAt)}</span>
                  </div>
                  <p className={styles.noteText}>{note.note}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <textarea
            className={styles.addNoteInput}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={t('paymentVerification.notePlaceholder')}
            rows={2}
            maxLength={1000}
            disabled={isSubmitting}
          />
          <button
            className={styles.addNoteButton}
            onClick={handleAddNote}
            disabled={!noteText.trim() || isSubmitting}
          >
            {isSubmitting ? <Loader2 size={16} className={styles.spinnerIcon} /> : <Send size={16} />}
            {t('paymentVerification.addNote')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentRecordNotesModal;
